import { useState, useEffect, useCallback, useMemo } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useAuthFetch } from '../lib/useAuthFetch'
import { Card, Pill, SectionLabel } from '../components/ui'
import type { PillTone } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/annotations) ──────────────────────────────────
interface Rival {
  id: string
  name: string
}
interface Annotation {
  id: string
  author_id: string | null
  entity_type: string | null
  entity_id: string | null
  body: string
  assignee_id: string | null
  resolved: boolean
  created_at: string
  target_label: string | null
  target_kind: 'competitor' | 'location' | 'alert' | 'product' | null
  target_location_name?: string | null
}

type StatusFilter = 'open' | 'resolved' | 'all'

// ─── Formatters (all user-local) ─────────────────────────────────────────────
function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}
function groupLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'Unknown date'
  const today = startOfLocalDay(new Date())
  const that = startOfLocalDay(d)
  const dayMs = 86_400_000
  const md = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  if (that === today) return `Today — ${md}`
  if (that === today - dayMs) return `Yesterday — ${md}`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
function relTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const diff = Date.now() - d.getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24 && startOfLocalDay(d) === startOfLocalDay(new Date())) return `${hr}h ago`
  return d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })
}

// Deterministic avatar tint from an author id, drawn from the CannaSpy palette.
const AVATAR_TINTS = ['var(--accent)', 'var(--warm)', 'var(--slate)', 'var(--rose)']
function avatarBg(id: string | null): string {
  if (!id) return 'var(--slate)'
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_TINTS[h % AVATAR_TINTS.length]
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Target chip tone by kind — rivals read rose (a competitor), locations accent,
// alerts warm.
function targetTone(kind: Annotation['target_kind']): PillTone {
  if (kind === 'competitor') return 'blocking'
  if (kind === 'location') return 'accent'
  if (kind === 'alert') return 'warning'
  if (kind === 'product') return 'accent'
  return 'neutral'
}

export default function TeamAnnotations() {
  const authFetch = useAuthFetch()
  const { user } = useUser()

  const [annotations, setAnnotations] = useState<Annotation[]>([])
  const [rivals, setRivals] = useState<Rival[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [rivalFilter, setRivalFilter] = useState<string>('') // competitor_id ('' = all)
  // Default to the full record: this is a collaboration surface, not an open-item
  // queue. Opening on 'open' hid every note whenever nothing was unresolved.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [rivalOpen, setRivalOpen] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)

  // Compose
  const [cpRivalId, setCpRivalId] = useState<string>('') // '' = general (no target)
  const [cpRivalOpen, setCpRivalOpen] = useState(false)
  const [cpNote, setCpNote] = useState('')
  const [posting, setPosting] = useState(false)

  const [toast, setToast] = useState<string | null>(null)
  const [toastColor, setToastColor] = useState('var(--accent)')
  function showToast(msg: string, color = 'var(--accent)') {
    setToast(msg)
    setToastColor(color)
    setTimeout(() => setToast(null), 2600)
  }

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    authFetch(`${API}/api/v1/annotations`)
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: { annotations: Annotation[]; rivals: Rival[] } }) => {
        setAnnotations(d.data.annotations ?? [])
        setRivals(d.data.rivals ?? [])
      })
      .catch((e) => setError(e?.message || 'Could not load annotations.'))
      .finally(() => setLoading(false))
  }, [authFetch])

  useEffect(() => { load() }, [load])

  // Author display — resolve the current user's name for their own notes; other
  // teammates render honestly as "Teammate" (no member directory exists yet).
  const meName = user?.fullName || user?.firstName || user?.primaryEmailAddress?.emailAddress || 'You'
  const authorName = useCallback(
    (id: string | null) => (id && user?.id && id === user.id ? meName : 'Teammate'),
    [user?.id, meName]
  )

  const openCount = useMemo(() => annotations.filter((a) => !a.resolved).length, [annotations])
  const resolvedCount = annotations.length - openCount

  const filtered = useMemo(() => {
    let rows = annotations
    if (statusFilter === 'open') rows = rows.filter((a) => !a.resolved)
    else if (statusFilter === 'resolved') rows = rows.filter((a) => a.resolved)
    if (rivalFilter) rows = rows.filter((a) => a.entity_id === rivalFilter)
    return rows
  }, [annotations, statusFilter, rivalFilter])

  // Group filtered rows by local calendar day, newest first (rows arrive DESC).
  const groups = useMemo(() => {
    const out: { label: string; items: Annotation[] }[] = []
    for (const a of filtered) {
      const label = groupLabel(a.created_at)
      const last = out[out.length - 1]
      if (last && last.label === label) last.items.push(a)
      else out.push({ label, items: [a] })
    }
    return out
  }, [filtered])

  const rivalFilterLabel = rivalFilter ? (rivals.find((r) => r.id === rivalFilter)?.name ?? 'Rival') : 'All rivals'
  const statusLabel = statusFilter === 'open' ? 'Open' : statusFilter === 'resolved' ? 'Resolved' : 'All'
  const cpRivalLabel = cpRivalId ? (rivals.find((r) => r.id === cpRivalId)?.name ?? 'Rival') : 'General — no rival'

  function toggleResolved(a: Annotation) {
    const next = !a.resolved
    setAnnotations((prev) => prev.map((x) => (x.id === a.id ? { ...x, resolved: next } : x)))
    authFetch(`${API}/api/v1/annotations/${a.id}/resolved`, {
      method: 'PATCH',
      body: JSON.stringify({ resolved: next }),
    })
      .then(async (r) => { if (!r.ok) throw new Error() })
      .then(() => showToast(next ? 'Marked resolved' : 'Reopened'))
      .catch(() => {
        setAnnotations((prev) => prev.map((x) => (x.id === a.id ? { ...x, resolved: !next } : x)))
        showToast("Couldn't update status — retry in a moment", 'var(--warm)')
      })
  }

  function postAnnotation() {
    const note = cpNote.trim()
    if (!note) { showToast('Add a note before posting', 'var(--warm)'); return }
    setPosting(true)
    authFetch(`${API}/api/v1/annotations`, {
      method: 'POST',
      body: JSON.stringify({
        body: note,
        entity_type: cpRivalId ? 'competitor' : null,
        entity_id: cpRivalId || null,
      }),
    })
      .then(async (r) => { if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) } })
      .then(() => {
        setCpNote('')
        setCpRivalId('')
        showToast('Annotation posted — shared with your team')
        load()
      })
      .catch((e) => showToast(e?.message || "Couldn't post — retry in a moment", 'var(--warm)'))
      .finally(() => setPosting(false))
  }

  const closeDropdowns = () => { setRivalOpen(false); setStatusOpen(false); setCpRivalOpen(false) }

  const ddStyle: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 5px)', left: 0,
    background: 'var(--surface)', border: '1px solid var(--border-2)',
    borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
    zIndex: 200, minWidth: 180, maxHeight: 280, overflowY: 'auto', padding: 5,
  }
  const ddOpt = (selected: boolean): React.CSSProperties => ({
    padding: '7px 10px', borderRadius: 6, fontSize: 12,
    color: selected ? 'var(--accent)' : 'var(--text-1)', fontWeight: selected ? 700 : 400, cursor: 'pointer',
  })

  const hasAny = annotations.length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }} onClick={closeDropdowns}>
      {/* Topbar */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface)' }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>Team Annotations</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 1, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Shared context on competitor intelligence</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={() => document.getElementById('cp-note-input')?.focus()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', border: '1.5px solid var(--accent)', background: 'var(--accent)', color: '#fff' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 12, height: 12 }}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            New annotation
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ padding: '8px 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* Rival filter */}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Rival</span>
        <div style={{ position: 'relative' }}>
          <div onClick={(e) => { e.stopPropagation(); setRivalOpen((o) => !o); setStatusOpen(false) }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, border: '1.5px solid', background: rivalFilter ? 'var(--accent)' : 'var(--surface)', borderColor: rivalFilter ? 'var(--accent)' : 'var(--border-2)', color: rivalFilter ? '#fff' : 'var(--text-1)', cursor: 'pointer' }}>
            {rivalFilterLabel}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ width: 10, height: 10, opacity: 0.6 }}><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {rivalOpen && (
            <div style={ddStyle}>
              <div onClick={(e) => { e.stopPropagation(); setRivalFilter(''); setRivalOpen(false) }} style={ddOpt(!rivalFilter)}>All rivals {!rivalFilter ? '✓' : ''}</div>
              {rivals.map((r) => (
                <div key={r.id} onClick={(e) => { e.stopPropagation(); setRivalFilter(r.id); setRivalOpen(false) }} style={ddOpt(r.id === rivalFilter)}>
                  {r.name} {r.id === rivalFilter ? '✓' : ''}
                </div>
              ))}
              {rivals.length === 0 && <div style={{ padding: '7px 10px', fontSize: 11, color: 'var(--text-3)' }}>No tracked rivals yet</div>}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />

        {/* Status filter */}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Status</span>
        <div style={{ position: 'relative' }}>
          <div onClick={(e) => { e.stopPropagation(); setStatusOpen((o) => !o); setRivalOpen(false) }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)', cursor: 'pointer' }}>
            {statusLabel}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ width: 10, height: 10, opacity: 0.6 }}><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {statusOpen && (
            <div style={ddStyle}>
              {(['open', 'resolved', 'all'] as StatusFilter[]).map((opt) => (
                <div key={opt} onClick={(e) => { e.stopPropagation(); setStatusFilter(opt); setStatusOpen(false) }} style={ddOpt(opt === statusFilter)}>
                  {opt === 'open' ? 'Open' : opt === 'resolved' ? 'Resolved' : 'All'} {opt === statusFilter ? '✓' : ''}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          {loading ? 'Loading…' : `${openCount} open · ${resolvedCount} resolved`}
        </div>
      </div>

      {/* Two-pane */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Annotation list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', minHeight: 0 }}>
          {loading && (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-3)', padding: 40, textAlign: 'center' }}>
              Pulling shared annotations…
            </div>
          )}

          {!loading && error && (
            <Card style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)', marginBottom: 6 }}>Couldn't load annotations</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>{error}</div>
              <button onClick={load} style={{ padding: '7px 14px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', border: '1.5px solid var(--accent)', background: 'var(--accent)', color: '#fff' }}>Retry</button>
            </Card>
          )}

          {!loading && !error && !hasAny && (
            <Card style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8 }}>No annotations yet</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6, maxWidth: 400, margin: '0 auto' }}>
                Add the first note to start building shared context on your rivals. Everyone with access sees what you flag, spot, or question.
              </div>
            </Card>
          )}

          {!loading && !error && hasAny && filtered.length === 0 && (
            <Card style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6 }}>Nothing matches these filters</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                {statusFilter !== 'all' ? `No ${statusFilter} annotations` : 'No annotations'}{rivalFilter ? ` for ${rivalFilterLabel}` : ''}.
              </div>
            </Card>
          )}

          {!loading && !error && groups.map((group) => (
            <div key={group.label} style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <SectionLabel style={{ margin: 0 }}>{group.label}</SectionLabel>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>

              {group.items.map((a) => (
                <Card key={a.id} style={{ marginBottom: 10, padding: '13px 16px', opacity: a.resolved ? 0.72 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: avatarBg(a.author_id), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0, marginTop: 1 }}>
                      {initials(authorName(a.author_id))}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>{authorName(a.author_id)}</span>
                        {a.target_label && (
                          <Pill variant="chip" tone={targetTone(a.target_kind)}>{a.target_label}</Pill>
                        )}
                        {/* A price task is about a SKU AT a store — the store is
                            half the meaning, so it rides alongside the product. */}
                        {a.target_kind === 'product' && a.target_location_name && (
                          <Pill variant="chip" tone="neutral">{a.target_location_name}</Pill>
                        )}
                        {a.assignee_id && (
                          <span data-testid="annotation-assignee">
                            <Pill variant="chip" tone="warning">{`Assigned · ${a.assignee_id}`}</Pill>
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', marginTop: 1 }} title={new Date(a.created_at).toLocaleString()}>
                        {relTime(a.created_at)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-1)', lineHeight: 1.6, marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{a.body}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9 }}>
                        <span
                          onClick={(e) => { e.stopPropagation(); toggleResolved(a) }}
                          style={{ marginLeft: 'auto', cursor: 'pointer' }}
                          title={a.resolved ? 'Reopen' : 'Mark resolved'}>
                          <Pill variant="chip" tone={a.resolved ? 'accent' : 'danger'}>{a.resolved ? 'Resolved' : 'Open'}</Pill>
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ))}
        </div>

        {/* Compose panel */}
        <div style={{ width: 340, flexShrink: 0, background: 'var(--surface)', borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>New Annotation</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>Visible to all team members with access</div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {/* Rival */}
            <div style={{ marginBottom: 14 }}>
              <SectionLabel style={{ marginBottom: 6 }}>Rival</SectionLabel>
              <div style={{ position: 'relative' }}>
                <div onClick={(e) => { e.stopPropagation(); setCpRivalOpen((o) => !o) }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, border: '1.5px solid', background: cpRivalId ? 'var(--accent)' : 'var(--surface-2)', borderColor: cpRivalId ? 'var(--accent)' : 'var(--border-2)', color: cpRivalId ? '#fff' : 'var(--text-1)', cursor: 'pointer' }}>
                  {cpRivalLabel}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ width: 10, height: 10, opacity: 0.6 }}><polyline points="6 9 12 15 18 9" /></svg>
                </div>
                {cpRivalOpen && (
                  <div style={{ ...ddStyle, width: '100%' }}>
                    <div onClick={(e) => { e.stopPropagation(); setCpRivalId(''); setCpRivalOpen(false) }} style={ddOpt(!cpRivalId)}>General — no rival {!cpRivalId ? '✓' : ''}</div>
                    {rivals.map((r) => (
                      <div key={r.id} onClick={(e) => { e.stopPropagation(); setCpRivalId(r.id); setCpRivalOpen(false) }} style={ddOpt(r.id === cpRivalId)}>
                        {r.name} {r.id === cpRivalId ? '✓' : ''}
                      </div>
                    ))}
                    {rivals.length === 0 && <div style={{ padding: '7px 10px', fontSize: 11, color: 'var(--text-3)' }}>No tracked rivals yet — file as general</div>}
                  </div>
                )}
              </div>
            </div>

            {/* Note */}
            <div style={{ marginBottom: 14 }}>
              <SectionLabel style={{ marginBottom: 6 }}>Note</SectionLabel>
              <textarea
                id="cp-note-input"
                value={cpNote}
                onChange={(e) => setCpNote(e.target.value)}
                placeholder="What did you observe? What action, if any, should the team take?"
                style={{ width: '100%', padding: '9px 10px', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 12, outline: 'none', resize: 'none', minHeight: 100, lineHeight: 1.5, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={postAnnotation}
              disabled={posting}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '9px 13px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, cursor: posting ? 'default' : 'pointer', opacity: posting ? 0.65 : 1, fontFamily: 'var(--sans)', border: '1.5px solid var(--accent)', background: 'var(--accent)', color: '#fff' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 12, height: 12 }}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
              {posting ? 'Posting…' : 'Post annotation'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '11px 18px', fontSize: 12, fontWeight: 600, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', zIndex: 9000, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-1)' }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: toastColor, flexShrink: 0 }} />
          {toast}
        </div>
      )}
    </div>
  )
}
